import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";

const STATUS_STYLES = {
  intent:        { bg: "bg-white/5",       border: "border-white/20",       text: "text-white/60",       label: "Intent" },
  claimed_paid:  { bg: "bg-yellow-400/10", border: "border-yellow-400/40",  text: "text-yellow-300",     label: "Awaiting verification" },
  confirmed:     { bg: "bg-emerald-400/10",border: "border-emerald-400/40", text: "text-emerald-300",    label: "Confirmed" },
  rejected:      { bg: "bg-red-400/10",    border: "border-red-400/40",     text: "text-red-300",        label: "Rejected" },
};

const formatAmount = (amt, cur) => {
  const n = Math.round(Number(amt) || 0);
  return `${cur || "IDR"} ${n.toLocaleString("id-ID")}`;
};

const QrisOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("claimed_paid");
  const [busyRef, setBusyRef] = useState(null);

  const load = (status) => {
    setLoading(true);
    const q = status === "all" ? "" : `?status=${status}`;
    api
      .get(`/admin/qris-orders${q}`)
      .then(({ data }) => setOrders(data.orders || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(filter);
  }, [filter]);

  const act = async (reference, action) => {
    const note = action === "reject"
      ? window.prompt("Reason for rejection (optional):") || ""
      : "";
    setBusyRef(reference);
    try {
      await api.post(`/admin/qris-orders/${reference}/${action}`, { note });
      toast.success(`Order ${reference} ${action === "confirm" ? "confirmed" : "rejected"}`);
      load(filter);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally {
      setBusyRef(null);
    }
  };

  const filterTabs = [
    { k: "claimed_paid", l: "Awaiting verification" },
    { k: "confirmed", l: "Confirmed" },
    { k: "rejected", l: "Rejected" },
    { k: "intent", l: "Intent only" },
    { k: "all", l: "All" },
  ];

  return (
    <div data-testid="qris-orders" className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-heading text-3xl text-ivory">QRIS Orders</h2>
          <p className="text-white/50 text-sm mt-2 max-w-2xl">
            Buyers who paid via QRIS appear here once they confirm payment. Verify the amount &amp; reference in your Bank Mandiri / QRIS dashboard, then click <strong className="text-emerald-300">Confirm</strong> to release the order — or <strong className="text-red-300">Reject</strong> if no payment was received.
          </p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-white/10 pb-4 flex-wrap">
        {filterTabs.map((t) => (
          <button
            key={t.k}
            data-testid={`qris-filter-${t.k}`}
            onClick={() => setFilter(t.k)}
            className={`font-accent text-[10px] tracking-[0.28em] uppercase px-4 py-2.5 ${
              filter === t.k
                ? "text-gold border-b border-gold -mb-[17px] pb-4"
                : "text-white/50 hover:text-white"
            }`}
          >
            {t.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-white/40 text-sm">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="bg-onyx border border-white/10 p-12 text-center text-white/40">
          No QRIS orders in this view.
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const s = STATUS_STYLES[o.status] || STATUS_STYLES.intent;
            return (
              <div
                key={o.reference}
                data-testid={`qris-order-${o.reference}`}
                className="bg-onyx border border-white/10 p-5 grid grid-cols-1 lg:grid-cols-12 gap-4 items-center"
              >
                <div className="lg:col-span-3">
                  <div className="font-mono text-gold">{o.reference}</div>
                  <div className="text-xs text-white/40 mt-1">
                    {o.created_at ? new Date(o.created_at).toLocaleString() : "—"}
                  </div>
                  <span
                    className={`inline-block mt-2 px-2 py-1 border ${s.bg} ${s.border} ${s.text} font-accent text-[9px] tracking-[0.24em] uppercase`}
                  >
                    {s.label}
                  </span>
                </div>

                <div className="lg:col-span-4">
                  <div className="font-heading text-ivory">{o.gemstone_name}</div>
                  <div className="font-heading text-xl text-gold mt-1">
                    {formatAmount(o.amount_idr, o.currency)}
                  </div>
                </div>

                <div className="lg:col-span-3 text-sm">
                  <div className="text-ivory">{o.buyer_name || "—"}</div>
                  <div className="text-white/60 text-xs mt-1">{o.buyer_email}</div>
                  {o.buyer_phone && (
                    <div className="text-white/60 text-xs">{o.buyer_phone}</div>
                  )}
                </div>

                <div className="lg:col-span-2 flex flex-col gap-2">
                  {o.status === "claimed_paid" && (
                    <>
                      <button
                        data-testid={`qris-confirm-${o.reference}`}
                        disabled={busyRef === o.reference}
                        onClick={() => act(o.reference, "confirm")}
                        className="font-accent text-[10px] tracking-[0.24em] uppercase border border-emerald-400 text-emerald-300 px-3 py-2 hover:bg-emerald-400 hover:text-black disabled:opacity-50"
                      >
                        Confirm
                      </button>
                      <button
                        data-testid={`qris-reject-${o.reference}`}
                        disabled={busyRef === o.reference}
                        onClick={() => act(o.reference, "reject")}
                        className="font-accent text-[10px] tracking-[0.24em] uppercase border border-red-400/60 text-red-300 px-3 py-2 hover:bg-red-400 hover:text-black disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {o.status === "intent" && (
                    <div className="text-xs text-white/40 italic text-right">
                      Buyer opened QR but hasn't confirmed payment yet.
                    </div>
                  )}
                  {o.admin_note && (
                    <div className="text-xs text-white/50 italic">{o.admin_note}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default QrisOrders;
